import type Phaser from 'phaser';

import { PixelText } from './pixel-text.js';

const FILL = 0x12121c;
const RIM = 0x6a6a88;
const TEXT = 0xf0f0f6;
/** The panel lets the ground show a little, like the chat's lines. */
const ALPHA = 0.88;
const PAD_X = 3;
const PAD_Y = 2;
/** Rows of the point under the panel, aimed at the head. */
const TIP = 3;

export interface SpeechBubbleOptions {
  scene: Phaser.Scene;
  atlasKey: string;
  fontKey: string;
}

/**
 * A spoken line over a head: a dark panel with cut corners, a light rim and a
 * point towards the speaker, drawn on whole pixels so it scales with the
 * camera like the sprites (CLAUDE.md rule 6). Its origin is the tip of the
 * point; the panel grows upward from there.
 */
export class SpeechBubble {
  readonly container: Phaser.GameObjects.Container;
  private readonly frame: Phaser.GameObjects.Graphics;
  private readonly text: PixelText;

  constructor({ scene, atlasKey, fontKey }: SpeechBubbleOptions) {
    this.frame = scene.add.graphics().setAlpha(ALPHA);
    this.text = new PixelText({ scene, atlasKey, fontKey, color: TEXT });
    this.container = scene.add.container(0, 0, [this.frame, this.text.container]);
  }

  setText(value: string): void {
    this.text.setText(value);
    const textHeight =
      this.text.lineCount * this.text.lineStep - (this.text.lineStep - this.text.glyphHeight);
    const width = this.text.width + 2 * PAD_X + 2;
    const height = textHeight + 2 * PAD_Y + 2;
    const left = -Math.round(width / 2);
    const top = -TIP - height;

    const g = this.frame.clear();
    // Rim with its corner pixels cut, then the panel inside it.
    g.fillStyle(RIM);
    g.fillRect(left + 1, top, width - 2, height);
    g.fillRect(left, top + 1, width, height - 2);
    g.fillStyle(FILL);
    g.fillRect(left + 1, top + 1, width - 2, height - 2);
    // The point: the panel opens into it, rimmed on both sides.
    for (let row = 0; row < TIP; row++) {
      const half = TIP - 1 - row;
      const y = -TIP + row;
      g.fillStyle(RIM);
      g.fillRect(-half - 1, y, 2 * half + 3, 1);
      g.fillStyle(FILL);
      g.fillRect(-half, y, 2 * half + 1, 1);
    }
    g.fillStyle(RIM);
    g.fillRect(0, 0, 1, 1);
    g.fillStyle(FILL);
    g.fillRect(-(TIP - 1), top + height - 1, 2 * TIP - 1, 1);

    this.text.setPosition(0, top + 1 + PAD_Y);
  }

  setVisible(visible: boolean): void {
    this.container.setVisible(visible);
  }

  /** Puts the last pixel of the point at (x, y). */
  setPosition(x: number, y: number): void {
    this.container.setPosition(Math.round(x), Math.round(y));
  }
}
